/**
 * WhatsApp Dashboard - Context Provider
 *
 * Provider per la configurazione centralizzata del dashboard.
 * Gestisce API URL, intervalli di refresh e altre impostazioni globali.
 *
 * @example
 * ```tsx
 * import { WhatsAppProvider, WhatsAppDashboard } from './whatsapp-dashboard';
 *
 * function App() {
 *   return (
 *     <WhatsAppProvider config={{ apiBaseUrl: 'https://api.myserver.com/api' }}>
 *       <WhatsAppDashboard />
 *     </WhatsAppProvider>
 *   );
 * }
 * ```
 */

import React, { createContext, useContext, useMemo } from 'react';
import type { WhatsAppConfig } from './types';

/** Configurazione di default */
const DEFAULT_CONFIG: WhatsAppConfig = {
  apiBaseUrl: '/api',
  refreshInterval: 5000,
  theme: 'dark'
};

/** Context per la configurazione WhatsApp */
const WhatsAppContext = createContext<WhatsAppConfig>(DEFAULT_CONFIG);

/**
 * Props del WhatsAppProvider
 */
interface WhatsAppProviderProps {
  /** Configurazione del dashboard */
  config: Partial<WhatsAppConfig>;
  /** Componenti figli */
  children: React.ReactNode;
}

/**
 * Provider per la configurazione del WhatsApp Dashboard.
 *
 * Avvolgi i componenti del dashboard con questo provider per configurare:
 * - `apiBaseUrl`: URL base delle API (es: '/api' o 'https://myserver.com/api')
 * - `refreshInterval`: Intervallo di polling in ms (default: 5000)
 * - `theme`: Tema visivo 'dark' | 'light' (default: 'dark')
 *
 * @param props - Props del provider
 * @returns Provider configurato
 *
 * @example
 * ```tsx
 * <WhatsAppProvider config={{
 *   apiBaseUrl: 'https://api.example.com',
 *   refreshInterval: 10000
 * }}>
 *   <WhatsAppDashboard />
 * </WhatsAppProvider>
 * ```
 */
export function WhatsAppProvider({ config, children }: WhatsAppProviderProps) {
  const mergedConfig = useMemo(() => ({
    ...DEFAULT_CONFIG,
    ...config
  }), [config]);

  return (
    <WhatsAppContext.Provider value={mergedConfig}>
      {children}
    </WhatsAppContext.Provider>
  );
}

/**
 * Hook per accedere alla configurazione WhatsApp.
 *
 * Deve essere usato all'interno di un WhatsAppProvider.
 *
 * @returns Configurazione corrente
 * @throws Error se usato fuori dal provider
 *
 * @example
 * ```tsx
 * function MyComponent() {
 *   const { apiBaseUrl } = useWhatsAppConfig();
 *   // usa apiBaseUrl per le chiamate API
 * }
 * ```
 */
export function useWhatsAppConfig(): WhatsAppConfig {
  const context = useContext(WhatsAppContext);
  if (!context) {
    throw new Error('useWhatsAppConfig deve essere usato dentro WhatsAppProvider');
  }
  return context;
}

export { WhatsAppContext };
